import { ReactNode, useCallback, useMemo, useState } from 'react';

import { Box, Button, styled } from '@mui/material';

import { ActionLabels } from '@sbv-clients/util-misc';

import {
  Dialog,
  DialogActionsContainer,
  DialogContent,
  DialogHeader,
} from '../../components';
import useToggleModal from '../use-toggle-modal/use-toggle-modal';

export type ConfirmationModalConfig = {
  title: string;
  content: ReactNode;
};

export const useConfirmationModal = () => {
  const { open, openModal, closeModal } = useToggleModal();
  const [modalConfig, setModalConfig] =
    useState<ConfirmationModalConfig | null>(null);
  const [resolveReject, setResolveReject] = useState<{
    resolve: (() => void) | null;
    reject: (() => void) | null;
  }>({ resolve: null, reject: null });

  const onCancelAction = useCallback(() => {
    resolveReject.reject?.();
    closeModal();
  }, [resolveReject, closeModal]);

  const handleOkAction = useCallback(() => {
    resolveReject.resolve?.();
    closeModal();
  }, [resolveReject, closeModal]);

  const modal = useMemo(() => {
    if (!modalConfig) return null;

    return (
      <Dialog open={open} fullWidth>
        <DialogHeader title={modalConfig.title} onClose={onCancelAction} />
        <DialogContent sx={{ mt: 6 }}>{modalConfig.content}</DialogContent>
        <DialogActionsContainer>
          <Button variant="outlined" onClick={onCancelAction}>
            {ActionLabels.Cancel}
          </Button>
          <Button variant="contained" onClick={handleOkAction}>
            {ActionLabels.Continue}
          </Button>
        </DialogActionsContainer>
      </Dialog>
    );
  }, [open, modalConfig, onCancelAction, handleOkAction]);

  return {
    modal,
    openModal: useCallback(
      (config: ConfirmationModalConfig): Promise<void> => {
        return new Promise((resolve, reject) => {
          setModalConfig(config);
          setResolveReject({ resolve, reject });
          openModal();
        });
      },
      [openModal]
    ),
  };
};

export default useConfirmationModal;
